// SpeakersEdit.jsx - Complete Final Code with Room Type Dropdown
import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import "./SpeakersComponent.scss";

/* small inline styles */
const styles = `
.flags-row { display:flex; align-items:center; gap:20px; flex-wrap:wrap; margin:16px 0; }
.flag { display:flex; align-items:center; gap:8px; margin:0; }
.form-group { margin-bottom: 12px; }
.error-box { background:#fff0f0; border:1px solid #ffdddd; color:#900; padding:10px; border-radius:6px; margin:10px 0; }
.info-box { background:#f0fff4; border:1px solid #ddffdd; color:#070; padding:10px; border-radius:6px; margin:10px 0; }
.debug-pre { background:#f7f7f7; padding:8px; border-radius:6px; max-height:240px; overflow:auto; white-space:pre-wrap; font-size:12px; }
.small-muted { color:#666; font-size:13px; }
.form-container { scroll-behavior: smooth; }
`;

/* Allowed dropdowns */
const ALLOWED = {
  flight_ticket: ["Business Class", "Economy Class","None"],
  transportation: ["One Way", "Two Ways","None"],
  visa_fees: ["Covered", "Not Covered"],
  // conference_dinner handled manually
  social_trip: ["Included", "Not Included"],
  registration_fee: ["Covered", "Not Covered"],
};

const ALLOWED_ROOM_TYPES = ["Single", "Double","Triple","None"];

/* Salutation options: use value without trailing period for stability */
const SALUTATIONS = ["Dr", "Prof", "Mr", "Mrs", "Ms"]; // labels and values same

const DEFAULT_SPEAKER = {
  salutation: "",
  name: "",
  passwordh: "",
  is_online_approved: false,
  is_certificate_active: false,
  airport_pickup: false,
  flight_ticket: "",
  transportation: "",
  visa_fees: "",
  conference_dinner: "",
  social_trip: "",
  registration_fee: "",
  conference_dinner_persons: 0,
  accommodation_room_type: "",
  accommodation_nights: 0,
  other: "",
  room_type: "",
  nights_covered: 0,
  is_visa_payment_required: false,
  conference_id: "",
  user: { name: "", email: "" },
};

function beautifyFieldLabel(field) {
  return field.replaceAll("_", " ").replace(/\b\w/g, (c) => c.toUpperCase()).trim();
}

/* Normalize salutation to one of SALUTATIONS */
function normalizeSalutation(rawVal) {
  const v = (rawVal ?? "").toString().trim();
  if (!v) return "";
  // Split if combined like "Dr John" -> take first token
  const first = v.split(/\s+/)[0];
  // Remove trailing dot and lowercase for match
  const cleaned = first.replace(/\.+$/, "").trim();
  // Try case-insensitive match with SALUTATIONS
  const found = SALUTATIONS.find((s) => s.toLowerCase() === cleaned.toLowerCase());
  return found || ""; // ensure exact option value so <select value> matches
}

function extractMessagesFromApiData(data) {
  const msgs = [];
  if (!data) {
    msgs.push("Unknown server error.");
    return msgs;
  }
  if (typeof data === "string" && data.trim()) {
    msgs.push(data);
    return Array.from(new Set(msgs));
  }
  if (data.details && typeof data.details === "object") {
    for (const [field, arr] of Object.entries(data.details)) {
      if (Array.isArray(arr)) {
        arr.forEach((m) => msgs.push(`${beautifyFieldLabel(field)}: ${String(m).trim()}`));
      } else if (typeof arr === "string") {
        msgs.push(`${beautifyFieldLabel(field)}: ${arr}`);
      } else {
        msgs.push(`${beautifyFieldLabel(field)}: ${JSON.stringify(arr)}`);
      }
    }
  }
  if (data.errors && typeof data.errors === "object") {
    for (const [field, arr] of Object.entries(data.errors)) {
      if (Array.isArray(arr)) {
        arr.forEach((m) => msgs.push(`${beautifyFieldLabel(field)}: ${String(m).trim()}`));
      } else if (typeof arr === "string") {
        msgs.push(`${beautifyFieldLabel(field)}: ${arr}`);
      } else {
        msgs.push(`${beautifyFieldLabel(field)}: ${JSON.stringify(arr)}`);
      }
    }
  }
  if (data.message && typeof data.message === "string") msgs.push(data.message);
  if (data.error && typeof data.error === "string") msgs.push(data.error);
  if (msgs.length === 0 && typeof data === "object") {
    for (const [k, v] of Object.entries(data)) {
      if (typeof v === "string" && v.trim()) {
        msgs.push(`${beautifyFieldLabel(k)}: ${v}`);
      }
    }
  }
  if (msgs.length === 0) msgs.push("Unknown server error.");
  return Array.from(new Set(msgs));
}

const SpeakersEdit = () => {
  const { id } = useParams();
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const [speaker, setSpeaker] = useState({ ...DEFAULT_SPEAKER });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [inlineErrors, setInlineErrors] = useState([]);
  const [inlineInfo, setInlineInfo] = useState([]);
  const [rawDebug, setRawDebug] = useState(null);

  const dinnerPersonsRef = useRef(null);

  const API_BASE = "https://eventscons.com/eventbackendnew/api";

  useEffect(() => {
    const fetchSpeaker = async () => {
      setLoading(true);
      setInlineErrors([]);
      setInlineInfo([]);
      setRawDebug(null);

      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setInlineErrors(["Please login first."]);
          toast.error("Please login first.");
          if (mountedRef.current) setLoading(false);
          return;
        }

        const res = await axios.get(`${API_BASE}/admin/speakers/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const raw = res.data?.speaker ?? res.data?.data ?? res.data ?? {};
        if (mountedRef.current) setRawDebug({ fetchResponse: raw });

        const prefName = (raw?.name ?? raw?.user?.name ?? "").toString();
        const mapFee = (v) => {
          if (v === "Included") return "Covered";
          if (v === "Not Included") return "Not Covered";
          return v ?? "";
        };

        if (mountedRef.current) {
          setSpeaker((prev) => ({
            ...prev,
            // Normalize salutation from various sources
            salutation: normalizeSalutation(
              raw?.salutation ??
                raw?.Salutation ??
                raw?.title ??
                raw?.Title ??
                raw?.user?.salutation ??
                raw?.user?.title ??
                ""
            ),
            name: prefName,
            passwordh: raw?.passwordh ?? "",
            is_online_approved: !!raw?.is_online_approved,
            is_certificate_active: !!raw?.is_certificate_active,
            flight_ticket: raw?.flight_ticket ?? "",
            transportation: raw?.transportation ?? "",
            visa_fees: raw?.visa_fees ?? "",
            conference_dinner: raw?.conference_dinner ?? "",
            social_trip: raw?.social_trip ?? "",
            registration_fee: mapFee(raw?.registration_fee),
            conference_dinner_persons:
              typeof raw?.conference_dinner_persons === "number"
                ? raw.conference_dinner_persons
                : Number(raw?.conference_dinner_persons) || 0,
            accommodation_room_type: raw?.accommodation_room_type ?? "",
            accommodation_nights:
              typeof raw?.accommodation_nights === "number"
                ? raw.accommodation_nights
                : Number(raw?.accommodation_nights) || 0,
            other: raw?.other ?? "",
            room_type: raw?.room_type ?? "",
            nights_covered:
              typeof raw?.nights_covered === "number"
                ? raw.nights_covered
                : Number(raw?.nights_covered) || 0,
            is_visa_payment_required: !!raw?.is_visa_payment_required,
            airport_pickup:
              raw?.airport_pickup !== undefined
                ? !!raw.airport_pickup
                : !!raw?.airport_pickup_active,
            conference_id: raw?.conference_id ?? "",
            user: { name: raw?.user?.name || "", email: raw?.user?.email || "" },
          }));
        }

        toast.success("Speaker data loaded");
      } catch (err) {
        const msgs = extractMessagesFromApiData(err?.response?.data);
        setInlineErrors(msgs);
        setRawDebug({ fetchError: err?.response?.data ?? err?.message ?? err });
        if (msgs.length > 0) toast.error(msgs[0]);
        else toast.error("Error loading speaker data");
        console.error("Fetch speaker error:", err);
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    };

    if (id) fetchSpeaker();
    else {
      setInlineErrors(["No speaker id provided."]);
      setLoading(false);
    }
  }, [id]);

  // Scroll persons on dinner include
  useEffect(() => {
    if (speaker.conference_dinner === "Included" && dinnerPersonsRef.current) {
      requestAnimationFrame(() => {
        const el = dinnerPersonsRef.current;
        if (!el) return;
        el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });
        setTimeout(() => el.focus?.(), 0);
      });
    }
  }, [speaker.conference_dinner]);

  const onChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    // user.* fields
    if (name.startsWith("user.")) {
      const key = name.split(".").pop();
      setSpeaker((p) => ({ ...p, user: { ...p.user, [key]: value } }));
      return;
    }

    // file
    if (type === "file") {
      const f = files && files[0];
      setSpeaker((p) => ({ ...p, [name]: f }));
      return;
    }

    // checkbox
    if (type === "checkbox") {
      setSpeaker((p) => ({ ...p, [name]: !!checked }));
      return;
    }

    // numeric fields
    const numericFields = new Set([
      "accommodation_nights",
      "conference_dinner_persons",
      "nights_covered",
    ]);
    if (numericFields.has(name)) {
      const num = value === "" ? 0 : Number(value);
      setSpeaker((p) => ({ ...p, [name]: Number.isNaN(num) ? 0 : num }));
      return;
    }

    // conference dinner switch resets persons
    if (name === "conference_dinner" && value !== "Included") {
      setSpeaker((p) => ({ ...p, conference_dinner: value, conference_dinner_persons: 0 }));
      return;
    }

    // salutation normalize so select shows selected
    if (name === "salutation") {
      setSpeaker((p) => ({ ...p, salutation: normalizeSalutation(value) }));
      return;
    }

    // default
    setSpeaker((p) => ({ ...p, [name]: value }));
  };

  const validateBeforeSubmit = () => {
    const messages = [];
    Object.entries(ALLOWED).forEach(([key, allowed]) => {
      const v = speaker[key];
      if (!v || !allowed.includes(v)) {
        messages.push(`${beautifyFieldLabel(key)}: Please select a valid value.`);
      }
    });
    if (!speaker.conference_dinner || !["Included", "Not Included"].includes(speaker.conference_dinner)) {
      messages.push("Conference Dinner: Please select a valid value.");
    }
    if (speaker.conference_dinner === "Included" && (!speaker.conference_dinner_persons || Number(speaker.conference_dinner_persons) < 1)) {
      messages.push("Conference Dinner: Please enter number of persons (>= 1).");
    }
    if (!speaker.name || speaker.name.trim().length === 0) {
      messages.push("Name is required.");
    }
    if (speaker.user?.email) {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!re.test(speaker.user.email)) messages.push("Email: Please enter a valid email address.");
    }
    if (ALLOWED_ROOM_TYPES.length > 0 && speaker.room_type && !ALLOWED_ROOM_TYPES.includes(speaker.room_type)) {
      messages.push(`Room Type: The selected room type is invalid. Allowed: ${ALLOWED_ROOM_TYPES.join(", ")}`);
    }
    return { ok: messages.length === 0, messages };
  };

 const buildPayload = () => {
  return {
    title: speaker.salutation || "",  // ✅ FIXED: Sirf salutation
    salutation: speaker.salutation || "",
    name: (speaker.name || speaker.user?.name || "").trim(),  // ✅ Name alag
    user_name: (speaker.name || speaker.user?.name || "").trim(),  // ✅ user_name bhi alag
    password: speaker.passwordh || "",
    passwordh: speaker.passwordh || "",
    is_online_approved: !!speaker.is_online_approved,
    is_certificate_active: !!speaker.is_certificate_active,
    flight_ticket: speaker.flight_ticket,
    accommodation_room_type: "Single",
    accommodation_nights: 1,
    transportation: speaker.transportation,
    visa_fees: speaker.visa_fees,
    conference_dinner: speaker.conference_dinner,
    conference_dinner_persons: speaker.conference_dinner === "Included" ? Number(speaker.conference_dinner_persons) || 0 : 0,
    social_trip: speaker.social_trip,
    registration_fee: speaker.registration_fee,
    other: speaker.other || "",
    room_type: speaker.room_type || "",
    nights_covered: Number(speaker.nights_covered) || 0,
    is_visa_payment_required: !!speaker.is_visa_payment_required,
    airport_pickup: !!speaker.airport_pickup,
    conference_id: speaker.conference_id || "",
    user: { name: speaker.user?.name || "", email: speaker.user?.email || "" },
  };
};


  const handleUpdate = async () => {
    setInlineErrors([]);
    setInlineInfo([]);
    setRawDebug(null);
    try {
      setSaving(true);
      const token = localStorage.getItem("token");
      if (!token) {
        setInlineErrors(["Please login first."]);
        toast.error("Please login first.");
        setSaving(false);
        return;
      }
      const check = validateBeforeSubmit();
      if (!check.ok) {
        const unique = Array.from(new Set(check.messages));
        setInlineErrors(unique);
        unique.forEach((m) => toast.error(m, { duration: 5000 }));
        setSaving(false);
        return;
      }
      const payload = buildPayload();
      setRawDebug({ sending: payload });
      const url = `${API_BASE}/admin/speakers/update-by-id/${id}`;
      const res = await axios.put(url, payload, { headers: { Authorization: `Bearer ${token}` } });
      const successMsg = res?.data?.message || "Speaker updated successfully";
      setInlineInfo([successMsg]);
      toast.success(successMsg);
      setRawDebug((p) => ({ ...(p || {}), response: res?.data }));
    } catch (err) {
      const data = err?.response?.data;
      const status = err?.response?.status;
      const extracted = extractMessagesFromApiData(data);
      const uniqueMessages = Array.from(new Set(extracted));
      if (uniqueMessages.length > 0) {
        uniqueMessages.forEach((m) => toast.error(m, { duration: 5000 }));
        setInlineErrors(uniqueMessages);
      } else {
        const fallback = err?.message || "Update failed!";
        toast.error(fallback);
        setInlineErrors([fallback]);
      }
      setRawDebug({ errorResponse: data ?? err?.message ?? err, status });
      console.error("Update error:", err);
    } finally {
      if (mountedRef.current) setSaving(false);
    }
  };

  const renderInlineErrors = () => {
    if (!inlineErrors || inlineErrors.length === 0) return null;
    return (
      <div className="error-box" role="alert" aria-live="polite">
        {inlineErrors.map((m, i) => (
          <div key={`err-${i}`} style={{ marginBottom: 6 }}>
            • {m}
          </div>
        ))}
      </div>
    );
  };

  const renderInlineInfo = () => {
    if (!inlineInfo || inlineInfo.length === 0) return null;
    return (
      <div className="info-box" role="status" aria-live="polite">
        {inlineInfo.map((m, i) => (
          <div key={`info-${i}`} style={{ marginBottom: 6 }}>
            • {m}
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ padding: 16 }}>
        <style>{styles}</style>
        <h3>Loading speaker...</h3>
        <div className="small-muted">Please wait — fetching speaker details.</div>
      </div>
    );
  }

  return (
    <div style={{ width: "100%", margin: "auto", padding: 14 }} className="form-container">
      <style>{styles}</style>

      <h6 className="headingaddspeakers">Edit Speaker Info</h6>

      {renderInlineErrors()}
      {renderInlineInfo()}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleUpdate();
        }}
        aria-label="Edit speaker form"
      >
        {/* Salutation + Name */}
        <div className="form-group">
          <label htmlFor="salutation" style={{ fontWeight: "bold" }}>
            Salutation / Title
          </label>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select
              id="salutation"
              name="salutation"
              value={speaker.salutation || ""}
              onChange={onChange}
              style={{ padding: 8, width: 160 }}
            >
              <option value="">Select</option>
              {SALUTATIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>

            <input
              id="name"
              name="name"
              type="text"
              value={speaker.name}
              onChange={onChange}
              style={{ flex: 1, padding: 8 }}
              placeholder="Enter full name"
              required
            />
          </div>
        </div>

        {/* Conference ID */}
        <div className="form-group">
          <label htmlFor="conference_id" style={{ fontWeight: "bold" }}>
            Conference ID
          </label>
          <input
            id="conference_id"
            name="conference_id"
            type="text"
            value={speaker.conference_id}
            onChange={onChange}
            style={{ width: "100%", padding: 8 }}
          />
        </div>

        {/* Email */}
        <div className="form-group">
          <label htmlFor="user.email" style={{ fontWeight: "bold" }}>
            Email
          </label>
          <input
            id="user.email"
            name="user.email"
            type="email"
            value={speaker.user?.email || ""}
            onChange={onChange}
            style={{ width: "100%", padding: 8 }}
          />
        </div>

        {/* Password */}
        <div className="form-group">
          <label htmlFor="passwordh" style={{ fontWeight: "bold" }}>
            Password
          </label>
          <input
            id="passwordh"
            name="passwordh"
            type="text"
            value={speaker.passwordh ?? ""}
            onChange={onChange}
            style={{ width: "100%", padding: 8 }}
          />
          <small className="small-muted">Leave blank to keep unchanged. Min 6 chars if changing.</small>
        </div>

        {/* Flags */}
        <div className="flags-row" style={{ marginBottom: 12 }}>
          <label className="flag">
            <input
              type="checkbox"
              name="is_online_approved"
              checked={!!speaker.is_online_approved}
              onChange={onChange}
            />
            <span>Online Approved</span>
          </label>

          <label className="flag">
            <input
              type="checkbox"
              name="is_certificate_active"
              checked={!!speaker.is_certificate_active}
              onChange={onChange}
            />
            <span>Certificate Active</span>
          </label>
        </div>

        {/* Conference Dinner first */}
        <div className="form-group">
          <label htmlFor="conference_dinner" style={{ fontWeight: "bold" }}>
            Conference Dinner *
          </label>
          <select
            id="conference_dinner"
            name="conference_dinner"
            value={speaker.conference_dinner || ""}
            onChange={onChange}
            style={{ width: "100%", padding: 8 }}
            required
          >
            <option value="">Select</option>
            <option value="Included">Included</option>
            <option value="Not Included">Not Included</option>
          </select>
        </div>

        {speaker.conference_dinner === "Included" && (
          <div className="form-group">
            <label htmlFor="conference_dinner_persons" style={{ fontWeight: "bold" }}>
              No. of persons
            </label>
            <input
              ref={dinnerPersonsRef}
              id="conference_dinner_persons"
              name="conference_dinner_persons"
              type="number"
              min={1}
              value={speaker.conference_dinner_persons ?? 1}
              onChange={onChange}
              style={{ width: "100%", padding: 8 }}
            />
          </div>
        )}

        {/* Remaining ALLOWED dropdowns */}
        {Object.entries(ALLOWED).map(([key, options]) => (
          <div className="form-group" key={key}>
            <label htmlFor={key} style={{ fontWeight: "bold" }}>
              {beautifyFieldLabel(key)} *
            </label>
            <select
              id={key}
              name={key}
              value={speaker[key] || ""}
              onChange={onChange}
              style={{ width: "100%", padding: 8 }}
              required
            >
              <option value="">Select</option>
              {options.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        ))}

        {/* Accommodation hidden */}
        <div className="form-group" style={{ display: 'none' }}>
          <label htmlFor="accommodation_room_type" style={{ fontWeight: "bold" }}>
            Accommodation Room Type
          </label>
          <input
            id="accommodation_room_type"
            name="accommodation_room_type"
            type="text"
            value={speaker.accommodation_room_type}
            onChange={onChange}
            style={{ width: "100%", padding: 8 }}
            placeholder="e.g., Deluxe Room"
          />
        </div>

        <div className="form-group" style={{ display: 'none' }}>
          <label htmlFor="accommodation_nights" style={{ fontWeight: "bold" }}>
            Accommodation Nights
          </label>
          <input
            id="accommodation_nights"
            name="accommodation_nights"
            type="number"
            min={0}
            value={speaker.accommodation_nights}
            onChange={onChange}
            style={{ width: "100%", padding: 8 }}
          />
        </div>

        {/* Room Type - DROPDOWN UPDATED ✅ */}
        <div className="form-group">
          <label htmlFor="room_type" style={{ fontWeight: "bold" }}>
            Room Type *
          </label>
          <select
            id="room_type"
            name="room_type"
            value={speaker.room_type || ""}
            onChange={onChange}
            style={{ width: "100%", padding: 8 }}
            required
          >
            <option value="">Select Room Type</option>
            <option value="Single">Single</option>
            <option value="Double">Double</option>
            <option value="Triple">Triple</option>
            <option value="None">None</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="nights_covered" style={{ fontWeight: "bold" }}>
            Nights Covered
          </label>
          <input
            id="nights_covered"
            name="nights_covered"
            type="number"
            min={0}
            value={speaker.nights_covered}
            onChange={onChange}
            style={{ width: "100%", padding: 8 }}
          />
        </div>

        <div className="form-group">
          <label htmlFor="other" style={{ fontWeight: "bold" }}>
            Other
          </label>
          <input
            id="other"
            name="other"
            type="text"
            value={speaker.other}
            onChange={onChange}
            style={{ width: "100%", padding: 8 }}
            placeholder="e.g., Airport lounge, early check-in"
          />
        </div>

        <div style={{ marginTop: 10 }}>
          <button
            className="submit-btn"
            type="submit"
            disabled={saving}
            style={{ width: "fit-content", padding: "10px 14px" }}
          >
            {saving ? "Updating..." : "Update Speaker"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SpeakersEdit;
